// src/services/mcpToolService.ts
// MCP工具服务 - 优化版本（统一初始化流程 + Google Scholar 工具）

export interface MCPTool {
  id: string;
  name: string;
  description: string;
  type: 'mcp' | 'builtin' | 'local';
  category: string;
  parameters: any;
  enabled: boolean;
  server_id?: string;
  server_name?: string;
  config?: any;
}

export interface MCPToolExecutionResult {
  success: boolean;
  result?: any;
  error?: string;
  tool_calls?: any[];
  raw_response?: string;
}

class MCPToolService {
  // 配置开关
  private config = {
    enableMCP: false,      // 🔴 禁用 MCP 工具
    enableLocal: true,     // 🟢 启用本地工具
  };
  
  private baseUrl = 'http://localhost:18085/api';
  private mcpTools: MCPTool[] = [];
  private localTools: MCPTool[] = [];
  private serviceAvailable = false;
  private initialized = false;  // ✅ 添加初始化标记

  /**
   * ✅ 统一初始化入口（只调用一次）
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      console.log('⚠️ [Tools] 已初始化，跳过重复初始化');
      return;
    }

    console.log('🚀 [Tools] 开始初始化工具服务...');

    try {
      // 步骤1: 加载本地工具（始终执行）
      if (this.config.enableLocal) {
        await this.loadLocalTools();
      } else {
        console.log('⚠️ [Tools] 本地工具已禁用');
      }

      // 步骤2: 检查并加载MCP工具（可选）
      if (this.config.enableMCP) {
        console.log('🔌 [MCP] 检查 MCP 服务状态...');
        this.serviceAvailable = await this.checkMCPStatusInternal();
        
        if (this.serviceAvailable) {
          console.log('🔍 [MCP] 开始加载 MCP 工具...');
          await this.loadMCPToolsInternal();
        } else {
          console.log('⚠️ [MCP] MCP 服务不可用，跳过加载');
        }
      } else {
        console.log('ℹ️ [MCP] MCP 工具已在配置中禁用');
        this.serviceAvailable = false;
      }

      this.initialized = true;
      
      // 输出统计信息
      const stats = this.getStatistics();
      console.log('✅ [Tools] 工具初始化完成:', {
        总计: stats.total,
        MCP工具: stats.mcp,
        本地工具: stats.local,
        已启用: stats.enabled
      });
      
    } catch (error) {
      console.error('❌ [Tools] 初始化失败:', error);
      // 即使失败也标记为已初始化，避免重试死循环
      this.initialized = true;
    }
  }

  /**
   * 内部方法：检查MCP服务状态（不输出重复日志）
   */
  private async checkMCPStatusInternal(): Promise<boolean> {
    try {
      const response = await fetch('http://localhost:18085/health', {
        signal: AbortSignal.timeout(3000) // 3秒超时
      });
      
      if (response.ok) {
        const data = await response.json();
        const isHealthy = data.status === 'healthy';
        console.log(`${isHealthy ? '✅' : '⚠️'} [MCP] 服务状态: ${data.status}`);
        return isHealthy;
      }
      return false;
    } catch (error) {
      console.error('❌ [MCP] 服务连接失败:', error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  }

  /**
   * 内部方法：加载MCP工具（不输出重复日志）
   */
  private async loadMCPToolsInternal(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/tools/mcp`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch MCP tools');
      }

      this.mcpTools = (data.tools || []).filter((t: MCPTool) => t.type === 'mcp');
      console.log(`✅ [MCP] 加载了 ${this.mcpTools.length} 个 MCP 工具`);
      
    } catch (error) {
      console.error('❌ [MCP] 加载失败:', error instanceof Error ? error.message : 'Unknown error');
      this.mcpTools = [];
    }
  }

  /**
   * ⚠️ 已废弃：使用 initialize() 代替
   * 保留此方法仅为向后兼容
   */
  async checkMCPStatus(): Promise<boolean> {
    if (!this.initialized) {
      console.warn('⚠️ [MCP] 建议先调用 initialize() 方法');
    }
    return this.serviceAvailable;
  }

  /**
   * ⚠️ 已废弃：使用 initialize() 代替
   * 保留此方法仅为向后兼容
   */
  async getAllMCPTools(): Promise<MCPTool[]> {
    if (!this.initialized) {
      console.warn('⚠️ [Tools] 建议先调用 initialize() 方法');
      await this.initialize();
    }
    return this.mcpTools;
  }

  /**
   * 加载本地工具
   */
  async loadLocalTools(): Promise<void> {
    try {
      console.log('🔍 [Tools] 加载本地工具...');
      
      // 1️⃣ 加载默认工具
      const defaultTools = this.getDefaultLocalTools();
      console.log(`📦 [Tools] 加载了 ${defaultTools.length} 个默认工具`);
      
      // 2️⃣ 从 localStorage 读取用户工具
      const storedToolsJson = localStorage.getItem('local_tools');
      
      if (storedToolsJson) {
        try {
          const storedTools = JSON.parse(storedToolsJson);
          const userTools = storedTools.filter((t: MCPTool) => 
            t.type === 'builtin' || t.type === 'local'
          );
          
          if (userTools.length > 0) {
            console.log(`👤 [Tools] 找到 ${userTools.length} 个用户工具`);
            this.localTools = this.mergeTools(defaultTools, userTools);
            console.log(`✅ [Tools] 合并后共 ${this.localTools.length} 个本地工具`);
          } else {
            this.localTools = defaultTools;
          }
          
        } catch (error) {
          console.error('⚠️ [Tools] localStorage 数据解析失败，使用默认工具');
          this.localTools = defaultTools;
        }
      } else {
        this.localTools = defaultTools;
      }
      
    } catch (error) {
      console.error('❌ [Tools] 加载本地工具失败:', error);
      this.localTools = this.getDefaultLocalTools();
    }
  }
  
  /**
   * 合并工具列表
   */
  private mergeTools(defaultTools: MCPTool[], userTools: MCPTool[]): MCPTool[] {
    const merged = [...defaultTools];
    
    userTools.forEach(userTool => {
      const existingIndex = merged.findIndex(t => t.id === userTool.id);
      
      if (existingIndex >= 0) {
        merged[existingIndex] = userTool;
      } else {
        merged.push(userTool);
      }
    });
    
    return merged;
  }

  /**
   * 获取默认的本地工具列表
   * ✅ 已添加 Google Scholar 工具
   */
  private getDefaultLocalTools(): MCPTool[] {
    return [
      // ==========================================
      // arXiv 工具
      // ==========================================
      {
        id: 'arxiv_search',
        name: 'arXiv论文搜索',
        description: '搜索arXiv预印本论文库，支持物理、数学等多个学科分类',
        type: 'builtin',
        category: 'search',
        parameters: {
          type: 'object',
          properties: {
            query: { type: 'string', description: '搜索关键词' },
            max_results: { type: 'number', default: 10, description: '最大结果数' },
            categories: { 
              type: 'array', 
              default: ['physics.optics'], 
              description: '分类列表' 
            },
            sort_by: { 
              type: 'string', 
              enum: ['relevance', 'submittedDate', 'lastUpdatedDate'],
              default: 'relevance',
              description: '排序方式'
            }
          },
          required: ['query']
        },
        enabled: true,
        config: {
          service: 'externalApiService',
          method: 'searchArxiv'
        }
      },
      
      // ==========================================
      // 增强论文搜索
      // ==========================================
      {
        id: 'paper_search_enhanced',
        name: '增强论文搜索',
        description: '支持期刊、日期、作者过滤的论文搜索',
        type: 'builtin',
        category: 'search',
        parameters: {
          type: 'object',
          properties: {
            query: { type: 'string', description: '搜索关键词' },
            limit: { type: 'number', default: 10, description: '返回结果数量' },
            scope: { type: 'number', default: 500, description: '搜索范围' },
            journal: { type: 'string', description: '期刊名称(可选)' },
            startDate: { type: 'string', description: '开始日期 YYYY-MM-DD(可选)' },
            endDate: { type: 'string', description: '结束日期 YYYY-MM-DD(可选)' },
            author: { type: 'string', description: '作者名(可选)' }
          },
          required: ['query']
        },
        enabled: true,
        config: {
          service: 'externalApiService',
          method: 'searchPapersEnhanced'
        }
      },
      
      // ==========================================
      // Google Scholar 工具组（新增）
      // ==========================================
      {
        id: 'scholar_search',
        name: 'Google Scholar 基础搜索',
        description: '基于Google Scholar的权威学术文献检索，支持引用数排序',
        type: 'builtin',
        category: 'search',
        parameters: {
          type: 'object',
          properties: {
            query: { type: 'string', description: '检索关键词（英文）' },
            max_results: { type: 'number', default: 15, description: '最大结果数（1-20）' },
            year_from: { type: 'number', description: '起始年份(可选)，如 2020' },
            year_to: { type: 'number', description: '结束年份(可选)，如 2024' },
            author: { type: 'string', description: '作者名(可选)，如 "John Smith"' },
            sort_by_date: { type: 'boolean', default: true, description: '按日期排序' }
          },
          required: ['query']
        },
        enabled: true,
        config: {
          service: 'literatureSearchService',
          method: 'search'
        }
      },
      
      {
        id: 'scholar_citing_papers',
        name: 'Google Scholar 引用分析',
        description: '查询引用某篇论文的所有文献（需要 cites_id）',
        type: 'builtin',
        category: 'search',
        parameters: {
          type: 'object',
          properties: {
            cites_id: { 
              type: 'string', 
              description: 'Google Scholar 引用ID（从检索结果中获取）' 
            },
            max_results: { type: 'number', default: 20, description: '最大结果数（1-20）' }
          },
          required: ['cites_id']
        },
        enabled: true,
        config: {
          service: 'literatureSearchService',
          method: 'getCitingPapers'
        }
      },
      
      {
        id: 'scholar_author_search',
        name: 'Google Scholar 作者检索',
        description: '检索特定作者的学术论文，生成学术画像',
        type: 'builtin',
        category: 'search',
        parameters: {
          type: 'object',
          properties: {
            author_name: { 
              type: 'string', 
              description: '作者名（英文），如 "Marko Loncar"' 
            },
            max_results: { type: 'number', default: 20, description: '最大结果数（1-20）' },
            year_from: { type: 'number', description: '起始年份(可选)' },
            year_to: { type: 'number', description: '结束年份(可选)' }
          },
          required: ['author_name']
        },
        enabled: true,
        config: {
          service: 'literatureSearchService',
          method: 'searchByAuthor'
        }
      },
      
      {
        id: 'scholar_year_range_search',
        name: 'Google Scholar 时间范围检索',
        description: '检索特定时间范围内的学术论文，分析研究趋势',
        type: 'builtin',
        category: 'search',
        parameters: {
          type: 'object',
          properties: {
            query: { type: 'string', description: '检索关键词（英文）' },
            year_from: { type: 'number', description: '起始年份，如 2020' },
            year_to: { type: 'number', description: '结束年份，如 2024' },
            max_results: { type: 'number', default: 15, description: '最大结果数（1-20）' }
          },
          required: ['query', 'year_from', 'year_to']
        },
        enabled: true,
        config: {
          service: 'literatureSearchService',
          method: 'searchByYearRange'
        }
      },
      
      {
        id: 'scholar_paper_versions',
        name: 'Google Scholar 论文版本查询',
        description: '查询同一论文的所有版本（预印本、会议、期刊等）',
        type: 'builtin',
        category: 'search',
        parameters: {
          type: 'object',
          properties: {
            cluster_id: { 
              type: 'string', 
              description: 'Google Scholar 聚类ID（从检索结果中获取）' 
            }
          },
          required: ['cluster_id']
        },
        enabled: true,
        config: {
          service: 'literatureSearchService',
          method: 'getPaperVersions'
        }
      },
      
      // ==========================================
      // 波导仿真工具
      // ==========================================
      {
        id: 'waveguide_simulation',
        name: '波导仿真计算',
        description: '单次波导参数仿真，计算有效折射率、群速度等',
        type: 'builtin',
        category: 'computation',
        parameters: {
          type: 'object',
          properties: {
            width_um: { type: 'number', description: '波导宽度(微米)' },
            wavelength_nm: { type: 'number', default: 1550, description: '波长(纳米)' },
            modes_number: { type: 'number', default: 3, description: '模式数量' }
          },
          required: ['width_um']
        },
        enabled: true,
        config: {
          service: 'externalApiService',
          method: 'simulateWaveguide'
        }
      },
      
      {
        id: 'waveguide_batch_width',
        name: '批量宽度扫描',
        description: '批量计算多个宽度的波导参数',
        type: 'builtin',
        category: 'computation',
        parameters: {
          type: 'object',
          properties: {
            widths_um: { 
              type: 'array', 
              items: { type: 'number' },
              description: '宽度数组(微米)，如 [0.5, 1.0, 1.5]' 
            },
            wavelength_nm: { type: 'number', default: 1550, description: '波长(纳米)' },
            modes_number: { type: 'number', default: 3, description: '模式数量' }
          },
          required: ['widths_um']
        },
        enabled: true,
        config: {
          service: 'externalApiService',
          method: 'batchSimulateWaveguide'
        }
      },
      
      {
        id: 'waveguide_batch_wavelength',
        name: '批量波长扫描',
        description: '批量计算多个波长的波导参数，用于色散分析',
        type: 'builtin',
        category: 'computation',
        parameters: {
          type: 'object',
          properties: {
            width_um: { type: 'number', description: '波导宽度(微米)' },
            wavelengths_nm: { 
              type: 'array', 
              items: { type: 'number' },
              description: '波长数组(纳米)，如 [1500, 1525, 1550, 1575, 1600]' 
            },
            modes_number: { type: 'number', default: 3, description: '模式数量' }
          },
          required: ['width_um', 'wavelengths_nm']
        },
        enabled: true,
        config: {
          service: 'externalApiService',
          method: 'batchWavelengthSimulation'
        }
      }
    ];
  }

  /**
   * 注册本地工具
   */
  registerLocalTool(tool: MCPTool): void {
    const existingIndex = this.localTools.findIndex(t => t.id === tool.id);
    
    if (existingIndex >= 0) {
      this.localTools[existingIndex] = tool;
      console.log(`✅ [Tools] 更新本地工具: ${tool.id}`);
    } else {
      this.localTools.push(tool);
      console.log(`✅ [Tools] 注册本地工具: ${tool.id}`);
    }
    
    this.saveLocalTools();
  }

  /**
   * 保存本地工具到 localStorage
   */
  private saveLocalTools(): void {
    try {
      localStorage.setItem('local_tools', JSON.stringify(this.localTools));
    } catch (error) {
      console.error('❌ [Tools] 保存失败:', error);
    }
  }

  /**
   * 删除本地工具
   */
  deleteLocalTool(toolId: string): boolean {
    const index = this.localTools.findIndex(t => t.id === toolId);
    if (index >= 0) {
      this.localTools.splice(index, 1);
      this.saveLocalTools();
      console.log(`✅ [Tools] 删除本地工具: ${toolId}`);
      return true;
    }
    return false;
  }

  /**
   * 获取所有工具（MCP + 本地）
   */
  getAllTools(): MCPTool[] {
    return [...this.mcpTools, ...this.localTools];
  }

  /**
   * 别名方法
   */
  listAllTools(): MCPTool[] {
    return this.getAllTools();
  }

  /**
   * 获取MCP工具
   */
  getCachedMCPTools(): MCPTool[] {
    return this.mcpTools;
  }

  /**
   * 获取本地工具
   */
  getLocalTools(): MCPTool[] {
    return this.localTools;
  }

  /**
   * 根据ID获取工具
   */
  getToolById(toolId: string): MCPTool | undefined {
    return this.getAllTools().find(t => t.id === toolId);
  }

  /**
   * 判断是否为MCP工具
   */
  isMCPTool(toolId: string): boolean {
    const tool = this.getToolById(toolId);
    return tool?.type === 'mcp';
  }

  /**
   * 执行MCP工具
   */
  async executeMCPTool(
    toolId: string,
    arguments_: Record<string, any>,
    query?: string
  ): Promise<MCPToolExecutionResult> {
    if (!this.config.enableMCP) {
      console.error('❌ [MCP] MCP 工具已禁用');
      return {
        success: false,
        error: 'MCP 工具已禁用'
      };
    }
    
    try {
      console.log('🚀 [MCP] 执行工具:', toolId);

      const requestBody: any = { arguments: arguments_ };
      if (query) requestBody.query = query;

      const response = await fetch(`${this.baseUrl}/tools/${toolId}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ [MCP] 执行成功');
      
      return result;
      
    } catch (error) {
      console.error('❌ [MCP] 执行失败:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * 获取统计信息
   */
  getStatistics() {
    return {
      total: this.mcpTools.length + this.localTools.length,
      mcp: this.mcpTools.length,
      local: this.localTools.length,
      enabled: this.getAllTools().filter(t => t.enabled).length,
      available: this.serviceAvailable,
      config: { ...this.config },
    };
  }

  /**
   * 刷新工具列表
   */
  async refreshTools(): Promise<void> {
    console.log('🔄 [Tools] 刷新工具列表...');
    this.initialized = false;
    await this.initialize();
  }
  
  /**
   * 更新配置
   */
  updateConfig(newConfig: Partial<typeof this.config>): void {
    console.log('⚙️ [Tools] 更新配置:', newConfig);
    this.config = { ...this.config, ...newConfig };
  }
  
  /**
   * 获取当前配置
   */
  getConfig() {
    return { ...this.config };
  }
}

// 导出单例
export const mcpToolService = new MCPToolService();